import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Save, X } from 'lucide-react';

const ReviewBusinessForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  isEditing 
}) => {
  
  const updateReviewLink = (index, field, value) => {
    const updated = [...formData.reviewLinks];
    updated[index][field] = value;
    setFormData({ ...formData, reviewLinks: updated });
  };

  const removeReviewLink = (index) => {
    const updated = formData.reviewLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, reviewLinks: updated });
  };

  const addReviewLink = () => {
    setFormData({
      ...formData,
      reviewLinks: [...formData.reviewLinks, { platform: 'google', url: '', priority: 0 }]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const updated = [...formData.socialLinks];
    updated[index][field] = value;
    setFormData({ ...formData, socialLinks: updated });
  };

  const removeSocialLink = (index) => {
    const updated = formData.socialLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, socialLinks: updated });
  };

  const addSocialLink = () => {
    setFormData({
      ...formData,
      socialLinks: [...formData.socialLinks, { platform: 'facebook', url: '' }]
    });
  };

  const updateCoupon = (index, field, value) => {
    const updated = [...formData.couponOffers];
    updated[index][field] = value;
    setFormData({ ...formData, couponOffers: updated });
  };

  const removeCoupon = (index) => {
    const updated = formData.couponOffers.filter((_, i) => i !== index);
    setFormData({ ...formData, couponOffers: updated });
  };

  const addCoupon = () => {
    setFormData({
      ...formData,
      couponOffers: [...formData.couponOffers, {
        code: '',
        description: '',
        businessName: '',
        expiryDate: '',
        termsAndConditions: ''
      }]
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">
          {isEditing ? 'Edit Business' : 'Add New Business'}
        </h2>
        <Button variant="ghost" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Business Name *</label>
              <Input
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                placeholder="e.g., Tasty Thai Restaurant"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Business Email (for feedback) *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="owner@business.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Business Type *</label>
              <select
                value={formData.businessType}
                onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                className="w-full p-2 border rounded-lg"
                required
              >
                <option value="restaurant">Restaurant</option>
                <option value="salon">Salon/Spa</option>
                <option value="retail">Retail Store</option>
                <option value="service">Service Business</option>
                <option value="cafe">Cafe/Coffee Shop</option>
                <option value="hotel">Hotel/Lodging</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tell customers about your business..."
                className="w-full p-2 border rounded-lg"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Logo URL (Optional)</label>
              <Input
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Primary Color</label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  className="w-20"
                />
                <Input
                  value={formData.primaryColor}
                  onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  placeholder="#3B82F6"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="active" className="text-sm font-medium">
                Active (visible to customers)
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Review Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Review Links *</span>
              <Button type="button" onClick={addReviewLink} size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add Link
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.reviewLinks.length === 0 && (
              <p className="text-gray-500 text-sm">No review links added yet. Add at least one.</p>
            )}
            
            {formData.reviewLinks.map((link, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">Review Link #{index + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeReviewLink(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div>
                  <label className="block text-sm mb-1">Platform</label>
                  <select
                    value={link.platform}
                    onChange={(e) => updateReviewLink(index, 'platform', e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="google">Google Reviews</option>
                    <option value="yelp">Yelp</option>
                    <option value="facebook">Facebook</option>
                    <option value="tripadvisor">TripAdvisor</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm mb-1">Review Page URL *</label>
                  <Input
                    value={link.url}
                    onChange={(e) => updateReviewLink(index, 'url', e.target.value)}
                    placeholder="https://www.google.com/search?q=your+business"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste the direct link to your review page
                  </p>
                </div>

                <div>
                  <label className="block text-sm mb-1">Priority (higher = shows first)</label>
                  <Input
                    type="number"
                    value={link.priority}
                    onChange={(e) => updateReviewLink(index, 'priority', parseInt(e.target.value))}
                    min="0"
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Social Media Links (Optional)</span>
              <Button type="button" onClick={addSocialLink} size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add Link
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.socialLinks.length === 0 && (
              <p className="text-gray-500 text-sm">No social links added yet.</p>
            )}
            
            {formData.socialLinks.map((link, index) => (
              <div key={index} className="flex gap-2">
                <select
                  value={link.platform}
                  onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                  className="p-2 border rounded w-32"
                >
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="tiktok">TikTok</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
                <Input
                  value={link.url}
                  onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                  placeholder="https://facebook.com/yourpage"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeSocialLink(index)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Coupon Offers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Coupon Offers (Optional)</span>
              <Button type="button" onClick={addCoupon} size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add Coupon
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.couponOffers.length === 0 && (
              <p className="text-gray-500 text-sm">No coupons added. Add coupons from partner businesses to reward reviewers!</p>
            )}
            
            {formData.couponOffers.map((coupon, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3 bg-yellow-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">Coupon #{index + 1}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCoupon(index)}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm mb-1">Coupon Code *</label>
                    <Input
                      value={coupon.code}
                      onChange={(e) => updateCoupon(index, 'code', e.target.value)}
                      placeholder="e.g., COFFEE2024"
                      className="bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-1">Partner Business Name *</label>
                    <Input
                      value={coupon.businessName}
                      onChange={(e) => updateCoupon(index, 'businessName', e.target.value)}
                      placeholder="e.g., Java Cafe"
                      className="bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1">Description *</label>
                  <Input
                    value={coupon.description}
                    onChange={(e) => updateCoupon(index, 'description', e.target.value)}
                    placeholder="e.g., Free coffee with any purchase"
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Expiry Date</label>
                  <Input
                    value={coupon.expiryDate}
                    onChange={(e) => updateCoupon(index, 'expiryDate', e.target.value)}
                    placeholder="e.g., December 31, 2024"
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Terms & Conditions</label>
                  <textarea
                    value={coupon.termsAndConditions}
                    onChange={(e) => updateCoupon(index, 'termsAndConditions', e.target.value)}
                    placeholder="e.g., One per customer. Cannot be combined with other offers."
                    className="w-full p-2 border rounded bg-white"
                    rows="2"
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-3">
          <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            {isEditing ? 'Update Business' : 'Create Business'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ReviewBusinessForm;
